import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fields = [
  { name: "name", label: "Full name", type: "text", ph: "Jane Doe", required: true },
  { name: "email", label: "Work email", type: "email", ph: "jane@city.gov", required: true },
  { name: "organization", label: "Organization", type: "text", ph: "City of Springfield / Operator", required: true },
  { name: "role", label: "Role", type: "text", ph: "Director of Mobility", required: false },
];

export const DemoForm = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thanks! Your demo request is in.", { description: "The GeoDyn team will reach out shortly." });
      setForm({ name: "", email: "", organization: "", role: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : typeof detail === "string" ? detail : null;
      toast.error("Couldn't send your request.", { description: msg || "Please check your details and try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="demo" data-testid="demo-section" className="relative overflow-hidden border-t border-white/5 py-28">
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(circle at 80% 20%, rgba(0,229,255,0.12) 0%, rgba(5,10,16,0) 55%)" }}
      />
      <div className="relative z-10 mx-auto grid max-w-6xl grid-cols-1 gap-14 px-6 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <Overline>Request a Demo</Overline>
          <h2 className="mt-4 font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
            Invest in the <span className="text-gradient">future of parking.</span>
          </h2>
          <p className="mt-5 max-w-md text-white/70">
            Join us in building the intelligence layer that powers the world's parking infrastructure.
            Tell us about your city or portfolio and we'll show you the impact.
          </p>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs uppercase tracking-wider text-white/50">
            <span>+15–30% Revenue</span>
            <span>−10–25% Congestion</span>
            <span>Real-Time Pricing</span>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="demo-form" className="rounded-2xl border border-white/10 bg-surface/50 p-8 backdrop-blur-sm sm:p-10">
            <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f.name}>
                  <label className="font-mono text-[11px] uppercase tracking-wider text-white/60">
                    {f.label}
                    {f.required && " *"}
                  </label>
                  <input
                    data-testid={`demo-input-${f.name}`}
                    type={f.type}
                    required={f.required}
                    value={form[f.name]}
                    onChange={set(f.name)}
                    placeholder={f.ph}
                    className="mt-2 w-full border-0 border-b border-white/20 bg-transparent px-0 py-2.5 text-white outline-none transition-colors placeholder:text-white/25 focus:border-brand-cyan focus:ring-0"
                  />
                </div>
              ))}
            </div>

            <div className="mt-7">
              <label className="font-mono text-[11px] uppercase tracking-wider text-white/60">Message</label>
              <textarea
                data-testid="demo-input-message"
                rows={3}
                value={form.message}
                onChange={set("message")}
                placeholder="What would you like to achieve with GeoDyn?"
                className="mt-2 w-full resize-none border-0 border-b border-white/20 bg-transparent px-0 py-2.5 text-white outline-none transition-colors placeholder:text-white/25 focus:border-brand-cyan focus:ring-0"
              />
            </div>

            <button
              type="submit"
              data-testid="demo-submit-btn"
              disabled={loading}
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-brand-green px-8 py-3.5 font-semibold text-black transition-transform hover:-translate-y-0.5 hover:bg-[#9fd94f] hover:shadow-[0_0_28px_rgba(141,198,63,0.5)] disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {loading ? "Sending..." : "Request a Demo"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
